import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { karmakandaAPI, bidhiTagAPI } from '../services/api';
import { Bidhi, BidhiHyperlinkBidhi, BidhiHyperlinkGrantha, BidhiHyperlinkSankalpa, BidhiLineGranthaLink, BidhiLineGranthaLevelLink, BidhiLineImageAssociation, BidhiLineMultipleBidhiLink, BidhiLineSankalpaLink, BidhiBrahmanYajamanKartabyaAssociation, BidhiReferenceType } from '../types';
import { normalizeBidhiFromApi, normalizeBidhiListFromApi } from '../utils/normalizeBidhiFromApi';
import {
  decryptEncryptedApiEnvelope,
  getSessionEncryptionKeyPair,
  isEncryptedApiEnvelope,
} from '../services/encryptedApiResponse';

interface BidhiState {
  bidhis: Bidhi[];
  currentBidhi: Bidhi | null;
  loading: boolean;
  currentBidhiLoading: boolean;
  error: string | null;
}

const initialState: BidhiState = {
  bidhis: [],
  currentBidhi: null,
  loading: false,
  currentBidhiLoading: false,
  error: null,
};

export const fetchBidhiById = createAsyncThunk('bidhi/fetchBidhiById', async (id: number) => {
  const response = await karmakandaAPI.getBidhiById(id);
  let data = response.data;
  if (isEncryptedApiEnvelope(data)) {
    const { privateKey } = await getSessionEncryptionKeyPair();
    data = await decryptEncryptedApiEnvelope(data, privateKey);
  }
  return normalizeBidhiFromApi(data);
});

export const fetchBidhis = createAsyncThunk('bidhi/fetchBidhis', async () => {
  try {
    const response = await karmakandaAPI.getAllBidhisDeduped();
    return normalizeBidhiListFromApi(response.data);
  } catch {
    // Fallback for older backends without dedupe support.
    const response = await karmakandaAPI.getAllBidhis();
    return normalizeBidhiListFromApi(response.data);
  }
});

export const addParent = createAsyncThunk(
  'bidhi/addParent',
  async ({ childId, parentId, order, startingPosition, lineId, entireBidhi, selectedBidhiLineIds, isTitleHidden }: { childId: number; parentId: number; order: number; startingPosition: number; lineId: number, entireBidhi: boolean, selectedBidhiLineIds: Array<number>, isTitleHidden?: boolean }) => {
    await karmakandaAPI.addParent(childId, parentId, { order, startingPosition, lineId, selectedBidhiLineIds, entireBidhi, isTitleHidden: Boolean(isTitleHidden) });
    return { childId, parentId, startingPosition, lineId, entireBidhi, selectedBidhiLineIds, hierarchyIsTitleHidden: Boolean(isTitleHidden) };
  }
);

export const removeParent = createAsyncThunk(
  'bidhi/removeParent',
  async ({ childId, parentId, lineId }: { childId: number; parentId: number; lineId: number }) => {
    await karmakandaAPI.removeParent(childId, parentId, lineId);
    return { childId, parentId, lineId };
  }
);

export const addLines = createAsyncThunk(
  'bidhi/addLines',
  async ({ bidhiId, lines }: { bidhiId: number; lines: { content: string; order: number; description?: string | null; isVisible?: boolean; link_only_line?: boolean; metadata?: Record<string, unknown> }[] }) => {
    const response = await karmakandaAPI.addLines(bidhiId, lines);
    return { bidhiId, lines: response.data };
  }
);

export const updateLine = createAsyncThunk(
  'bidhi/updateLine',
  async ({ lineId, content, order, description, metadata, isVisible, link_only_line }: {
    lineId: number;
    content: string;
    order: number;
    description?: string | null;
    metadata: Array<any>;
    isVisible?: boolean;
    link_only_line?: boolean;
  }) => {
    await karmakandaAPI.updateLine(lineId, { content, order, description, metadata, isVisible, link_only_line });
    return { lineId, content, order, description, metadata, isVisible, link_only_line };
  }
);

export const bulkUpdateLineOrder = createAsyncThunk(
  'bidhi/bulkUpdateLineOrder',
  async (updates: { bidhiLineId: number; changedOrder: number }[]) => {
    await karmakandaAPI.bulkUpdateBidhiLineOrder(updates);
    return updates;
  }
);

export const deleteLine = createAsyncThunk(
  'bidhi/deleteLine',
  async ({ bidhiId, lineId }: { bidhiId: number; lineId: number }) => {
    await karmakandaAPI.deleteLine(bidhiId, lineId);
    return { bidhiId, lineId };
  }
);

export const deleteBidhi = createAsyncThunk(
  'bidhi/deleteBidhi',
  async (id: number) => {
    await karmakandaAPI.deleteBidhiById(id);
    return id;
  }
);

export const createGranthaReference = createAsyncThunk(
  'bidhi/createGranthaReference',
  async ({ bidhiId, startPosition, referenceType, references, lineId, omOptions }: { bidhiId: number; startPosition: number; referenceType: BidhiReferenceType; references: any[]; lineId: number; omOptions: number }) => {
    const response = await karmakandaAPI.createGranthaReference({ bidhiId, startPosition, referenceType, references, lineId, omOptions });
    const responseData = response.data || {};
    return {
      bidhiId,
      startPosition,
      referenceType,
      references,
      lineId,
      omOptions,
      id: responseData.id,
      ...responseData,
    };
  }
);

export const updateBidhi = createAsyncThunk(
  'bidhi/updateBidhi',
  async ({ id, bidhiData }: { id: number; bidhiData: any }) => {
    const response = await karmakandaAPI.updateBidhi(id, bidhiData);
    return response.data;
  }
);

export const createBidhi = createAsyncThunk(
  'bidhi/createBidhi',
  async (bidhiData: any) => {
    const response = await karmakandaAPI.createBidhi(bidhiData);
    return response.data;
  }
);

export const deleteGranthaReference = createAsyncThunk(
  'bidhi/deleteGranthaReference',
  async ({ bidhiId, referenceId }: { bidhiId: number; referenceId: number }) => {
    await karmakandaAPI.deleteGranthaReference(referenceId);
    return { bidhiId, referenceId };
  }
);

export const createBidhiHyperlinkBidhi = createAsyncThunk(
  'bidhi/createBidhiHyperlinkBidhi',
  async (payload: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    references: {
      bidhiId: number;
      entireBidhi?: boolean;
      selectedBidhiLineIds?: number[];
    }[];
  }) => {
    const response = await karmakandaAPI.createBidhiHyperlinkBidhi(payload);
    const raw = response.data;
    const hyperlink: BidhiHyperlinkBidhi = {
      ...payload,
      ...(typeof raw === 'object' && raw !== null ? raw : {}),
      references:
        raw?.references && Array.isArray(raw.references) && raw.references.length > 0
          ? raw.references
          : payload.references,
    };
    return { bidhiId: payload.bidhiId, hyperlink };
  }
);

export const updateBidhiHyperlinkBidhi = createAsyncThunk(
  'bidhi/updateBidhiHyperlinkBidhi',
  async ({ bidhiId, id, data }: {
    bidhiId: number;
    id: number;
    data: Partial<{
      startPosition: number;
      endingPosition: number;
      shortcut: boolean;
      references: {
        bidhiId: number;
        entireBidhi?: boolean;
        selectedBidhiLineIds?: number[];
      }[];
    }>;
  }) => {
    const response = await karmakandaAPI.updateBidhiHyperlinkBidhi(id, data);
    return { bidhiId, id, hyperlink: response.data as BidhiHyperlinkBidhi };
  }
);

export const deleteBidhiHyperlinkBidhi = createAsyncThunk(
  'bidhi/deleteBidhiHyperlinkBidhi',
  async ({ bidhiId, id }: { bidhiId: number; id: number }) => {
    await karmakandaAPI.deleteBidhiHyperlinkBidhi(id);
    return { bidhiId, id };
  }
);

export const createBidhiHyperlinkSankalpa = createAsyncThunk(
  'bidhi/createBidhiHyperlinkSankalpa',
  async (payload: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    references: { sankalpaId: number }[];
  }) => {
    const response = await karmakandaAPI.createBidhiHyperlinkSankalpa(payload);
    const raw = response.data;
    const hyperlink: BidhiHyperlinkSankalpa = {
      ...payload,
      ...(typeof raw === 'object' && raw !== null ? raw : {}),
      references:
        raw?.references && Array.isArray(raw.references) && raw.references.length > 0
          ? raw.references
          : payload.references,
    };
    return { bidhiId: payload.bidhiId, hyperlink };
  }
);

export const updateBidhiHyperlinkSankalpa = createAsyncThunk(
  'bidhi/updateBidhiHyperlinkSankalpa',
  async ({ bidhiId, id, data }: {
    bidhiId: number;
    id: number;
    data: Partial<{
      startPosition: number;
      endingPosition: number;
      shortcut: boolean;
      references: { sankalpaId: number }[];
    }>;
  }) => {
    const response = await karmakandaAPI.updateBidhiHyperlinkSankalpa(id, data);
    return { bidhiId, id, hyperlink: response.data as BidhiHyperlinkSankalpa };
  }
);

export const deleteBidhiHyperlinkSankalpa = createAsyncThunk(
  'bidhi/deleteBidhiHyperlinkSankalpa',
  async ({ bidhiId, id }: { bidhiId: number; id: number }) => {
    await karmakandaAPI.deleteBidhiHyperlinkSankalpa(id);
    return { bidhiId, id };
  }
);

export const createBidhiHyperlinkGrantha = createAsyncThunk(
  'bidhi/createBidhiHyperlinkGrantha',
  async (payload: {
    bidhiId: number;
    lineId: number;
    startPosition: number;
    endingPosition: number;
    shortcut?: boolean;
    referenceType: string;
    references: BidhiHyperlinkGrantha['references'];
    notes?: string;
    omOptions?: number;
  }) => {
    const response = await karmakandaAPI.createBidhiHyperlinkGrantha(payload);
    const raw = response.data;
    const hyperlink: BidhiHyperlinkGrantha = {
      ...payload,
      ...(typeof raw === 'object' && raw !== null ? raw : {}),
      references:
        raw?.references && Array.isArray(raw.references) && raw.references.length > 0
          ? raw.references
          : payload.references,
    };
    return { bidhiId: payload.bidhiId, hyperlink };
  }
);

export const updateBidhiHyperlinkGrantha = createAsyncThunk(
  'bidhi/updateBidhiHyperlinkGrantha',
  async ({ bidhiId, id, data }: {
    bidhiId: number;
    id: number;
    data: Partial<{
      startPosition: number;
      endingPosition: number;
      shortcut: boolean;
      referenceType: string;
      references: BidhiHyperlinkGrantha['references'];
      notes?: string;
      omOptions?: number;
    }>;
  }) => {
    const response = await karmakandaAPI.updateBidhiHyperlinkGrantha(id, data);
    return { bidhiId, id, hyperlink: response.data as BidhiHyperlinkGrantha };
  }
);

export const deleteBidhiHyperlinkGrantha = createAsyncThunk(
  'bidhi/deleteBidhiHyperlinkGrantha',
  async ({ bidhiId, id }: { bidhiId: number; id: number }) => {
    await karmakandaAPI.deleteBidhiHyperlinkGrantha(id);
    return { bidhiId, id };
  }
);

export const removeTagFromBidhi = createAsyncThunk(
  'bidhi/removeTagFromBidhi',
  async ({ tagId, bidhiId }: { tagId: number; bidhiId: number }) => {
    await bidhiTagAPI.removeTagFromBidhi(tagId, bidhiId);
    return { tagId, bidhiId };
  }
);

const bidhiSlice = createSlice({
  name: 'bidhi',
  initialState,
  reducers: {
    setCurrentBidhi: (state, action: PayloadAction<Bidhi | null>) => {
      state.currentBidhi = action.payload;
      state.currentBidhiLoading = false;
    },
    addSamagriAssociation: (state, action) => {
      if (state.currentBidhi) {
        if (!state.currentBidhi.samagriAssociations) {
          state.currentBidhi.samagriAssociations = [];
        }
        state.currentBidhi.samagriAssociations.push(action.payload);
      }
    },
    removeSamagriAssociation: (state, action) => {
      if (state.currentBidhi && state.currentBidhi.samagriAssociations) {
        state.currentBidhi.samagriAssociations = state.currentBidhi.samagriAssociations.filter(
          assoc => assoc.id !== action.payload
        );
      }
    },
    addBrahmanYajamanKartabyaAssociation: (
      state,
      action: PayloadAction<{ bidhiId: number; association: BidhiBrahmanYajamanKartabyaAssociation }>,
    ) => {
      const { bidhiId, association } = action.payload;
      const updater = (bidhi: Bidhi) => {
        if (!bidhi.brahmanYajamanKartabyaAssociations) bidhi.brahmanYajamanKartabyaAssociations = [];
        const existingIdx = bidhi.brahmanYajamanKartabyaAssociations.findIndex((a) => a.id === association.id);
        if (existingIdx !== -1) {
          bidhi.brahmanYajamanKartabyaAssociations[existingIdx] = association;
        } else {
          bidhi.brahmanYajamanKartabyaAssociations.push(association);
        }
      };
      updateBidhiInTree(state.bidhis, bidhiId, updater);
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], bidhiId, updater);
      }
    },
    removeBrahmanYajamanKartabyaAssociation: (
      state,
      action: PayloadAction<{ associationId: number }>,
    ) => {
      const { associationId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        if (bidhi?.brahmanYajamanKartabyaAssociations) {
          bidhi.brahmanYajamanKartabyaAssociations = bidhi.brahmanYajamanKartabyaAssociations.filter(
            (a) => a.id !== associationId,
          );
        }
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
    addBidhiLineMultipleBidhiLink: (
      state,
      action: PayloadAction<{ ownerBidhiId: number; link: BidhiLineMultipleBidhiLink }>,
    ) => {
      const { ownerBidhiId, link } = action.payload;
      let matched = false;
      const updater = (bidhi: Bidhi) => {
        const line = bidhi.lines?.find((l) => l.id === link.bidhiLineId);
        if (!line) return;
        if (!line.multipleBidhiLinks) line.multipleBidhiLinks = [];
        const existingIdx = line.multipleBidhiLinks.findIndex((l) => l.id === link.id);
        if (existingIdx !== -1) {
          line.multipleBidhiLinks[existingIdx] = { ...line.multipleBidhiLinks[existingIdx], ...link };
        } else {
          line.multipleBidhiLinks.push(link);
        }
        line.multipleBidhiLinks.sort(
          (a, b) => a.position - b.position || (a.order || 0) - (b.order || 0),
        );
        matched = true;
      };
      updateBidhiInTree(state.bidhis, ownerBidhiId, updater);
      if (!matched) {
        const scanAll = (nodes: Bidhi[]) => {
          for (const node of nodes) {
            updater(node);
            if (node.children && node.children.length > 0) {
              scanAll(node.children);
            }
          }
        };
        scanAll(state.bidhis);
      }
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], ownerBidhiId, updater);
        if (!matched && state.currentBidhi.lines?.some((l) => l.id === link.bidhiLineId)) {
          updater(state.currentBidhi);
        }
      }
    },
    removeBidhiLineMultipleBidhiLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        bidhi?.lines?.forEach((line) => {
          if (line.multipleBidhiLinks) {
            line.multipleBidhiLinks = line.multipleBidhiLinks.filter((l) => l.id !== linkId);
          }
        });
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
    updateBidhiLineMultipleBidhiLinkOrder: (
      state,
      action: PayloadAction<{
        bidhiLineId: number;
        position: number;
        updates: { bidhiLineMultipleBidhiLinkId: number; changedOrder: number }[];
      }>,
    ) => {
      const { bidhiLineId, position, updates } = action.payload;
      const applyOrder = (bidhi: Bidhi | null) => {
        const line = bidhi?.lines?.find((l) => l.id === bidhiLineId);
        if (line?.multipleBidhiLinks) {
          updates.forEach(({ bidhiLineMultipleBidhiLinkId, changedOrder }) => {
            const link = line.multipleBidhiLinks!.find(
              (l) => l.id === bidhiLineMultipleBidhiLinkId && l.position === position,
            );
            if (link) link.order = changedOrder;
          });
          line.multipleBidhiLinks.sort(
            (a, b) => a.position - b.position || (a.order || 0) - (b.order || 0),
          );
        }
        bidhi?.children?.forEach((child) => applyOrder(child));
      };
      state.bidhis.forEach(applyOrder);
      if (state.currentBidhi) applyOrder(state.currentBidhi);
    },
    addBidhiLineSankalpaLink: (
      state,
      action: PayloadAction<{ ownerBidhiId: number; link: BidhiLineSankalpaLink }>,
    ) => {
      const { ownerBidhiId, link } = action.payload;
      let matched = false;
      const updater = (bidhi: Bidhi) => {
        const line = bidhi.lines?.find((l) => l.id === link.bidhiLineId);
        if (!line) return;
        if (!line.sankalpaLinks) line.sankalpaLinks = [];
        const existingIdx = line.sankalpaLinks.findIndex((l) => l.id === link.id);
        if (existingIdx !== -1) {
          line.sankalpaLinks[existingIdx] = { ...line.sankalpaLinks[existingIdx], ...link };
        } else {
          line.sankalpaLinks.push(link);
        }
        line.sankalpaLinks.sort((a, b) => a.position - b.position);
        matched = true;
      };
      updateBidhiInTree(state.bidhis, ownerBidhiId, updater);
      if (!matched) {
        const scanAll = (nodes: Bidhi[]) => {
          for (const node of nodes) {
            updater(node);
            if (node.children && node.children.length > 0) {
              scanAll(node.children);
            }
          }
        };
        scanAll(state.bidhis);
      }
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], ownerBidhiId, updater);
        if (!matched && state.currentBidhi.lines?.some((l) => l.id === link.bidhiLineId)) {
          updater(state.currentBidhi);
        }
      }
    },
    removeBidhiLineSankalpaLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        bidhi?.lines?.forEach((line) => {
          if (line.sankalpaLinks) {
            line.sankalpaLinks = line.sankalpaLinks.filter((l) => l.id !== linkId);
          }
        });
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
    addBidhiLineImageAssociation: (
      state,
      action: PayloadAction<{ ownerBidhiId: number; association: BidhiLineImageAssociation }>,
    ) => {
      const { ownerBidhiId, association } = action.payload;
      let matched = false;
      const updater = (bidhi: Bidhi) => {
        const line = bidhi.lines?.find((l) => l.id === association.bidhiLineId);
        if (!line) return;
        if (!line.imageAssociations) line.imageAssociations = [];
        const existingIndex = line.imageAssociations.findIndex((a) => a.id === association.id);
        if (existingIndex >= 0) {
          line.imageAssociations[existingIndex] = association;
        } else {
          line.imageAssociations.push(association);
        }
        line.imageAssociations.sort((a, b) => a.position - b.position);
        matched = true;
      };
      updateBidhiInTree(state.bidhis, ownerBidhiId, updater);
      if (!matched) {
        const scanAll = (nodes: Bidhi[]) => {
          for (const node of nodes) {
            updater(node);
            if (node.children && node.children.length > 0) {
              scanAll(node.children);
            }
          }
        };
        scanAll(state.bidhis);
      }
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], ownerBidhiId, updater);
        if (!matched && state.currentBidhi.lines?.some((l) => l.id === association.bidhiLineId)) {
          updater(state.currentBidhi);
        }
      }
    },
    removeBidhiLineImageAssociation: (state, action: PayloadAction<{ associationId: number }>) => {
      const { associationId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        bidhi?.lines?.forEach((line) => {
          if (line.imageAssociations) {
            line.imageAssociations = line.imageAssociations.filter((a) => a.id !== associationId);
          }
        });
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
    addBidhiLineGranthaLink: (
      state,
      action: PayloadAction<{ ownerBidhiId: number; link: BidhiLineGranthaLink }>,
    ) => {
      const { ownerBidhiId, link } = action.payload;
      let matched = false;
      const updater = (bidhi: Bidhi) => {
        const line = bidhi.lines?.find((l) => l.id === link.bidhiLineId);
        if (!line) return;
        if (!line.granthaLinks) line.granthaLinks = [];
        const existingIdx = line.granthaLinks.findIndex((l) => l.id === link.id);
        if (existingIdx !== -1) {
          line.granthaLinks[existingIdx] = { ...line.granthaLinks[existingIdx], ...link };
        } else {
          line.granthaLinks.push(link);
        }
        line.granthaLinks.sort(
          (a, b) =>
            (a.position ?? a.startingPosition ?? 0) - (b.position ?? b.startingPosition ?? 0),
        );
        matched = true;
      };
      updateBidhiInTree(state.bidhis, ownerBidhiId, updater);
      if (!matched) {
        const scanAll = (nodes: Bidhi[]) => {
          for (const node of nodes) {
            updater(node);
            if (node.children && node.children.length > 0) {
              scanAll(node.children);
            }
          }
        };
        scanAll(state.bidhis);
      }
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], ownerBidhiId, updater);
        if (!matched && state.currentBidhi.lines?.some((l) => l.id === link.bidhiLineId)) {
          updater(state.currentBidhi);
        }
      }
    },
    removeBidhiLineGranthaLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        bidhi?.lines?.forEach((line) => {
          if (line.granthaLinks) {
            line.granthaLinks = line.granthaLinks.filter((l) => l.id !== linkId);
          }
        });
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
    addBidhiLineGranthaLevelLink: (
      state,
      action: PayloadAction<{ ownerBidhiId: number; link: BidhiLineGranthaLevelLink }>,
    ) => {
      const { ownerBidhiId, link } = action.payload;
      let matched = false;
      const updater = (bidhi: Bidhi) => {
        const line = bidhi.lines?.find((l) => l.id === link.bidhiLineId);
        if (!line) return;
        if (!line.granthaLevelLinks) line.granthaLevelLinks = [];
        const existingIdx = line.granthaLevelLinks.findIndex((l) => l.id === link.id);
        if (existingIdx !== -1) {
          line.granthaLevelLinks[existingIdx] = { ...line.granthaLevelLinks[existingIdx], ...link };
        } else {
          line.granthaLevelLinks.push(link);
        }
        line.granthaLevelLinks.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        matched = true;
      };
      updateBidhiInTree(state.bidhis, ownerBidhiId, updater);
      if (!matched) {
        const scanAll = (nodes: Bidhi[]) => {
          for (const node of nodes) {
            updater(node);
            if (node.children && node.children.length > 0) {
              scanAll(node.children);
            }
          }
        };
        scanAll(state.bidhis);
      }
      if (state.currentBidhi) {
        updateBidhiInTree([state.currentBidhi], ownerBidhiId, updater);
        if (!matched && state.currentBidhi.lines?.some((l) => l.id === link.bidhiLineId)) {
          updater(state.currentBidhi);
        }
      }
    },
    removeBidhiLineGranthaLevelLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeFromBidhi = (bidhi: Bidhi | null) => {
        bidhi?.lines?.forEach((line) => {
          if (line.granthaLevelLinks) {
            line.granthaLevelLinks = line.granthaLevelLinks.filter((l) => l.id !== linkId);
          }
        });
        bidhi?.children?.forEach((child) => removeFromBidhi(child));
      };
      state.bidhis.forEach(removeFromBidhi);
      if (state.currentBidhi) removeFromBidhi(state.currentBidhi);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBidhiById.pending, (state) => {
        state.currentBidhiLoading = true;
        state.error = null;
      })
      .addCase(fetchBidhiById.fulfilled, (state, action) => {
        state.currentBidhiLoading = false;
        state.currentBidhi = action.payload;
        
        // Also update in bidhis array if it exists (keep full reference/hyperlink fields in sync)
        const updateBidhiInArray = (bidhis: Bidhi[]) => {
          bidhis.forEach((bidhi, index) => {
            if (bidhi.id === action.payload.id) {
              bidhis[index] = {
                ...bidhi,
                ...action.payload,
                // Preserve in-tree hierarchy attachment fields when present on the node
                startingPosition: bidhi.startingPosition,
                lineId: (bidhi as any).lineId,
                entireBidhi: (bidhi as any).entireBidhi,
                selectedBidhiLineIds: (bidhi as any).selectedBidhiLineIds,
                hierarchyIsTitleHidden: (bidhi as any).hierarchyIsTitleHidden,
                order: bidhi.order,
              } as Bidhi;
              return;
            }
            if (bidhi.children) {
              updateBidhiInArray(bidhi.children);
            }
          });
        };
        updateBidhiInArray(state.bidhis);
      })
      .addCase(fetchBidhiById.rejected, (state, action) => {
        state.currentBidhiLoading = false;
        state.error = action.error.message || 'Failed to fetch bidhi';
      })
      .addCase(fetchBidhis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBidhis.fulfilled, (state, action) => {
        state.loading = false;
        state.bidhis = action.payload;
      })
      .addCase(fetchBidhis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bidhis';
      })
      .addCase(addParent.fulfilled, (state, action) => {
        const { childId, parentId, startingPosition, lineId, entireBidhi, selectedBidhiLineIds, hierarchyIsTitleHidden } = action.payload;
        const child = findBidhiById(state.bidhis, childId) || (state.currentBidhi?.id === childId ? state.currentBidhi : null);
        
        if (child) {
          const newChild: any = {
            ...child,
            lineId,
            startingPosition,
            entireBidhi,
            selectedBidhiLineIds,
            hierarchyIsTitleHidden,
          };

          const updateParentInTree = (bidhis: Bidhi[]) => {
            bidhis.forEach((bidhi) => {
              if (bidhi.id === parentId) {
                if (!bidhi.children) bidhi.children = [];
                const existingIndex = bidhi.children.findIndex(
                  (c) => c.id === childId && (lineId != null ? (c as any).lineId === lineId : true)
                );
                if (existingIndex >= 0) {
                  bidhi.children[existingIndex] = { ...bidhi.children[existingIndex], ...newChild };
                } else {
                  bidhi.children.push(newChild);
                }

                if (lineId != null && bidhi.lines) {
                  const line = bidhi.lines.find((l) => l.id === lineId);
                  if (line) {
                    if (!line.embeddedBidhis) line.embeddedBidhis = [];
                    const embIndex = line.embeddedBidhis.findIndex((c) => c.id === childId);
                    if (embIndex >= 0) {
                      line.embeddedBidhis[embIndex] = { ...line.embeddedBidhis[embIndex], ...newChild };
                    } else {
                      line.embeddedBidhis.push(newChild);
                    }
                  }
                }
              }
              if (bidhi.children) {
                updateParentInTree(bidhi.children);
              }
            });
          };

          updateParentInTree(state.bidhis);
          if (state.currentBidhi) {
            updateParentInTree([state.currentBidhi]);
          }
        }
      })
      .addCase(removeParent.fulfilled, (state, action) => {
        const { childId, parentId, lineId } = action.payload;

        const updateParentInTree = (bidhis: Bidhi[]) => {
          bidhis.forEach((bidhi) => {
            if (bidhi.id === parentId) {
              if (bidhi.children) {
                bidhi.children = bidhi.children.filter((child) => {
                  const matchesChild = child.id === childId;
                  if (!matchesChild) return true;
                  if (lineId != null && (child as any).lineId != null) {
                    return (child as any).lineId !== lineId;
                  }
                  return false;
                });
              }
              bidhi.lines?.forEach((line) => {
                if (lineId == null || line.id === lineId) {
                  if (line.embeddedBidhis) {
                    line.embeddedBidhis = line.embeddedBidhis.filter((child) => child.id !== childId);
                  }
                }
              });
            }
            if (bidhi.children) {
              updateParentInTree(bidhi.children);
            }
          });
        };

        updateParentInTree(state.bidhis);
        if (state.currentBidhi) {
          updateParentInTree([state.currentBidhi]);
        }
      })
      .addCase(createGranthaReference.fulfilled, (state, action) => {
        const { bidhiId, startPosition, referenceType, references, lineId, omOptions, id } = action.payload;
        const newRef = { 
          id,
          startPosition, 
          referenceType, 
          references, 
          lineId, 
          omOptions 
        };
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.granthaReferences) bidhi.granthaReferences = [];
          const existingIdx = id != null ? bidhi.granthaReferences.findIndex((r) => r.id === id) : -1;
          if (existingIdx !== -1) {
            bidhi.granthaReferences[existingIdx] = { ...bidhi.granthaReferences[existingIdx], ...newRef };
          } else {
            bidhi.granthaReferences.push(newRef);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(updateBidhi.fulfilled, (state, action) => {
        const updatedBidhi = action.payload;
        
        // Update all instances of this bidhi (root level and children)
        const updateAllInstances = (bidhis: Bidhi[]) => {
          bidhis.forEach(bidhi => {
            if (bidhi.id === updatedBidhi.id) {
              bidhi.name = updatedBidhi.name;
              if (updatedBidhi.lines) bidhi.lines = updatedBidhi.lines;
              if (updatedBidhi.tags) bidhi.tags = updatedBidhi.tags;
            }
            if (bidhi.children) {
              updateAllInstances(bidhi.children);
            }
          });
        };
        
        updateAllInstances(state.bidhis);
      })
      .addCase(createBidhi.fulfilled, (state, action) => {
        state.bidhis.push(action.payload);
      })
      .addCase(addLines.fulfilled, (state, action) => {
        const { bidhiId, lines } = action.payload;
        const newLines = Array.isArray(lines?.lines) ? lines.lines : Array.isArray(lines) ? lines : null;
        if (newLines) {
          const updater = (bidhi: Bidhi) => {
            bidhi.lines = newLines;
          };
          updateBidhiInTree(state.bidhis, bidhiId, updater);
          if (state.currentBidhi) {
            updateBidhiInTree([state.currentBidhi], bidhiId, updater);
          }
        }
      })
      .addCase(updateLine.fulfilled, (state, action) => {
        const { lineId, content, order, description, metadata, isVisible, link_only_line } = action.payload;
        
        const updateLineInBidhis = (bidhis: Bidhi[]) => {
          bidhis.forEach(bidhi => {
            if (bidhi.lines) {
              const line = bidhi.lines.find(l => l.id === lineId);
              if (line) {
                line.content = content;
                line.metadata = metadata;
                (line as any).order = order;
                if (description !== undefined) line.description = description;
                if (isVisible !== undefined) line.isVisible = isVisible;
                if (link_only_line !== undefined) line.link_only_line = link_only_line;
              }
            }
            if (bidhi.children) {
              updateLineInBidhis(bidhi.children);
            }
          });
        };
        
        updateLineInBidhis(state.bidhis);
      })
      .addCase(deleteLine.fulfilled, (state, action) => {
        const { bidhiId, lineId } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.lines) {
            bidhi.lines = bidhi.lines.filter((line) => line.id !== lineId);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(bulkUpdateLineOrder.fulfilled, (state, action) => {
        const updates = action.payload;
        
        // Create a map of lineId to new order
        const orderMap = new Map<number, number>();
        updates.forEach(update => {
          orderMap.set(update.bidhiLineId, update.changedOrder);
        });
        
        // Update all lines in all bidhis with the new orders
        const updateLineOrders = (bidhis: Bidhi[]) => {
          bidhis.forEach(bidhi => {
            if (bidhi.lines) {
              bidhi.lines.forEach(line => {
                const newOrder = orderMap.get(line.id);
                if (newOrder !== undefined) {
                  (line as any).order = newOrder;
                }
              });
              // Sort lines by order after updating
              bidhi.lines.sort((a, b) => ((a as any).order || 0) - ((b as any).order || 0));
            }
            if (bidhi.children) {
              updateLineOrders(bidhi.children);
            }
          });
        };
        
        updateLineOrders(state.bidhis);
        if (state.currentBidhi) {
          updateLineOrders([state.currentBidhi]);
        }
      })
      .addCase(deleteBidhi.fulfilled, (state, action) => {
        const id = action.payload;
        state.bidhis = state.bidhis.filter(bidhi => bidhi.id !== id);
        // Also remove from children arrays and line embeddedBidhis
        const removeFromAll = (bidhis: Bidhi[]) => {
          bidhis.forEach(bidhi => {
            if (bidhi.children) {
              bidhi.children = bidhi.children.filter(child => child.id !== id);
              removeFromAll(bidhi.children);
            }
            bidhi.lines?.forEach((line) => {
              if (line.embeddedBidhis) {
                line.embeddedBidhis = line.embeddedBidhis.filter((child) => child.id !== id);
              }
            });
          });
        };
        removeFromAll(state.bidhis);
        if (state.currentBidhi) {
          if (state.currentBidhi.id === id) {
            state.currentBidhi = null;
          } else {
            removeFromAll([state.currentBidhi]);
          }
        }
      })
      .addCase(deleteGranthaReference.fulfilled, (state, action) => {
        const { bidhiId, referenceId } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.granthaReferences) {
            bidhi.granthaReferences = bidhi.granthaReferences.filter((ref) => ref.id !== referenceId);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(removeTagFromBidhi.fulfilled, (state, action) => {
        const { tagId, bidhiId } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.tags) {
            bidhi.tags = bidhi.tags.filter((tag) => tag.id !== tagId);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(createBidhiHyperlinkBidhi.fulfilled, (state, action) => {
        const { bidhiId, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkBidhis) bidhi.hyperlinkBidhis = [];
          const idx = bidhi.hyperlinkBidhis.findIndex((h) => h.id === hyperlink.id);
          if (idx !== -1) {
            bidhi.hyperlinkBidhis[idx] = { ...bidhi.hyperlinkBidhis[idx], ...hyperlink };
          } else {
            bidhi.hyperlinkBidhis.push(hyperlink);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(updateBidhiHyperlinkBidhi.fulfilled, (state, action) => {
        const { bidhiId, id, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkBidhis) return;
          const idx = bidhi.hyperlinkBidhis.findIndex((h) => h.id === id);
          if (idx !== -1) {
            bidhi.hyperlinkBidhis[idx] = { ...bidhi.hyperlinkBidhis[idx], ...hyperlink };
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(deleteBidhiHyperlinkBidhi.fulfilled, (state, action) => {
        const { bidhiId, id } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.hyperlinkBidhis) {
            bidhi.hyperlinkBidhis = bidhi.hyperlinkBidhis.filter((h) => h.id !== id);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(createBidhiHyperlinkSankalpa.fulfilled, (state, action) => {
        const { bidhiId, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkSankalpas) bidhi.hyperlinkSankalpas = [];
          const idx = bidhi.hyperlinkSankalpas.findIndex((h) => h.id === hyperlink.id);
          if (idx !== -1) {
            bidhi.hyperlinkSankalpas[idx] = { ...bidhi.hyperlinkSankalpas[idx], ...hyperlink };
          } else {
            bidhi.hyperlinkSankalpas.push(hyperlink);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(updateBidhiHyperlinkSankalpa.fulfilled, (state, action) => {
        const { bidhiId, id, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkSankalpas) return;
          const idx = bidhi.hyperlinkSankalpas.findIndex((h) => h.id === id);
          if (idx !== -1) {
            bidhi.hyperlinkSankalpas[idx] = { ...bidhi.hyperlinkSankalpas[idx], ...hyperlink };
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(deleteBidhiHyperlinkSankalpa.fulfilled, (state, action) => {
        const { bidhiId, id } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.hyperlinkSankalpas) {
            bidhi.hyperlinkSankalpas = bidhi.hyperlinkSankalpas.filter((h) => h.id !== id);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(createBidhiHyperlinkGrantha.fulfilled, (state, action) => {
        const { bidhiId, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkGranthas) bidhi.hyperlinkGranthas = [];
          const idx = bidhi.hyperlinkGranthas.findIndex((h) => h.id === hyperlink.id);
          if (idx !== -1) {
            bidhi.hyperlinkGranthas[idx] = { ...bidhi.hyperlinkGranthas[idx], ...hyperlink };
          } else {
            bidhi.hyperlinkGranthas.push(hyperlink);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(updateBidhiHyperlinkGrantha.fulfilled, (state, action) => {
        const { bidhiId, id, hyperlink } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (!bidhi.hyperlinkGranthas) return;
          const idx = bidhi.hyperlinkGranthas.findIndex((h) => h.id === id);
          if (idx !== -1) {
            bidhi.hyperlinkGranthas[idx] = { ...bidhi.hyperlinkGranthas[idx], ...hyperlink };
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      })
      .addCase(deleteBidhiHyperlinkGrantha.fulfilled, (state, action) => {
        const { bidhiId, id } = action.payload;
        const updater = (bidhi: Bidhi) => {
          if (bidhi.hyperlinkGranthas) {
            bidhi.hyperlinkGranthas = bidhi.hyperlinkGranthas.filter((h) => h.id !== id);
          }
        };
        updateBidhiInTree(state.bidhis, bidhiId, updater);
        if (state.currentBidhi) {
          updateBidhiInTree([state.currentBidhi], bidhiId, updater);
        }
      });
  },
});

function findBidhiById(bidhis: Bidhi[], id: number): Bidhi | null {
  for (const bidhi of bidhis) {
    if (bidhi.id === id) return bidhi;
  }
  for (const bidhi of bidhis) {
    if (bidhi.children && bidhi.children.length > 0) {
      const found = findBidhiById(bidhi.children, id);
      if (found) return found;
    }
  }
  return null;
}

function updateBidhiInTree(bidhis: Bidhi[], targetBidhiId: number, updater: (bidhi: Bidhi) => void) {
  for (const bidhi of bidhis) {
    if (bidhi.id === targetBidhiId) {
      updater(bidhi);
    }
    if (bidhi.children && bidhi.children.length > 0) {
      updateBidhiInTree(bidhi.children, targetBidhiId, updater);
    }
  }
}

function findBidhiInTree(bidhis: Bidhi[], id: number): Bidhi | null {
  return findBidhiById(bidhis, id);
}

function removeBidhiFromTree(bidhis: Bidhi[], id: number): boolean {
  for (let i = 0; i < bidhis.length; i++) {
    if (bidhis[i].id === id) {
      bidhis.splice(i, 1);
      return true;
    }
    if (bidhis[i].children && removeBidhiFromTree(bidhis[i].children!, id)) {
      return true;
    }
  }
  return false;
}

export const {
  setCurrentBidhi,
  addSamagriAssociation,
  removeSamagriAssociation,
  addBidhiLineMultipleBidhiLink,
  removeBidhiLineMultipleBidhiLink,
  updateBidhiLineMultipleBidhiLinkOrder,
  addBidhiLineSankalpaLink,
  removeBidhiLineSankalpaLink,
  addBidhiLineImageAssociation,
  removeBidhiLineImageAssociation,
  addBidhiLineGranthaLink,
  removeBidhiLineGranthaLink,
  addBidhiLineGranthaLevelLink,
  removeBidhiLineGranthaLevelLink,
} = bidhiSlice.actions;

export default bidhiSlice.reducer;